import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageSquare, ArrowLeft, Loader, Users, Plus } from 'lucide-react';
import { User, Conversation } from './types';
import { subscribeToConversations } from './services/chatService';
import { getUserPublicProfile, UserPublicProfile } from './services/userService';

export const ConversationsView = ({ 
  user,
  authReady,
  onBack, 
  onSelectConversation,
  onCreateGroup
}: { 
  user: User;
  authReady: boolean;
  onBack: () => void;
  onSelectConversation: (conversationId: string, otherUserId: string) => void;
  onCreateGroup?: () => void;
}) => {
  const { t } = useTranslation();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [userProfiles, setUserProfiles] = useState<Record<string, UserPublicProfile>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // 🔑 CRITICAL: Block ALL snapshots until auth is ready
    // Note: authReady should be passed as prop - checking from context for now
    // This will be fixed when authReady prop is passed
    if (!authReady) return;
    
    const unsubscribe = subscribeToConversations(user.id, (convs) => {
      setConversations(convs);
      setLoading(false);

      // Fetch user profiles for direct conversations only
      const profilePromises: Promise<void>[] = [];
      convs.forEach(conv => {
        if (conv.type === 'direct' || conv.type === 'private') {
          const otherUserId = conv.participants.find(uid => uid !== user.id);
          if (otherUserId && !userProfiles[otherUserId]) {
            profilePromises.push(
              getUserPublicProfile(otherUserId).then(profile => {
                setUserProfiles(prev => ({ ...prev, [otherUserId]: profile }));
              })
            );
          }
        }
      });
      // Fire and forget - don't block, but handle errors
      Promise.all(profilePromises).catch(err => {
        console.error('[CONVERSATIONS] Error loading user profiles:', err);
      });
    });

    return () => unsubscribe();
  }, [authReady, user.id]);

  const formatTimestamp = (timestamp: any): string => {
    if (!timestamp) return '';
    
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMins / 60);
      const diffDays = Math.floor(diffHours / 24);
      
      if (diffMins < 1) return t('time.just_now');
      if (diffMins < 60) return t('time.minutes_ago', { n: diffMins });
      if (diffHours < 24) return t('time.hours_ago', { n: diffHours });
      if (diffDays < 7) return t('time.days_ago', { n: diffDays });
      
      return date.toLocaleDateString('it-IT', { day: 'numeric', month: 'short' });
    } catch (error) {
      return '';
    }
  };

  const getOtherUser = (conversation: Conversation): UserPublicProfile | null => {
    const otherUserId = conversation.participants.find(uid => uid !== user.id);
    if (!otherUserId) return null;
    return userProfiles[otherUserId] || {
      uid: otherUserId,
      displayName: t('common.user_default'),
      photoURL: `https://api.dicebear.com/7.x/avataaars/svg?seed=${otherUserId}`,
      username: undefined
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
        <Loader className="animate-spin text-cyan-500 w-12 h-12" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f] flex flex-col">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-[#0a0a0f]/95 backdrop-blur-xl border-b border-slate-800">
        <div className="flex items-center justify-between p-4 md:p-6">
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-slate-800 rounded-xl text-slate-400 transition-colors"
            >
              <ArrowLeft size={20} />
            </button>
            <div className="flex items-center gap-3">
              <MessageSquare className="text-cyan-400" size={24} />
              <h1 className="text-2xl font-black text-white uppercase tracking-tight">{t('chat.conversations')}</h1>
            </div>
          </div>
          {onCreateGroup && (
            <button
              onClick={onCreateGroup}
              className="p-2 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400 rounded-xl transition-colors"
              title={t('chat.new_group')}
            >
              <Plus size={20} />
            </button>
          )}
        </div>
      </div>

      {/* Conversations List */}
      <div className="flex-1 overflow-y-auto">
        {conversations.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full py-20 px-4 text-center">
            <MessageSquare className="text-slate-700 mb-4" size={48} />
            <p className="text-slate-500 font-bold text-sm mb-2">{t('chat.no_conversations')}</p>
            <p className="text-slate-600 text-xs">{t('chat.no_conversations_desc')}</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-800">
            {conversations.map((conversation) => {
              // ✅ Safe fallback for unreadCount (handles legacy conversations)
              const unreadForCurrentUser = conversation.unreadCount?.[user.id] ?? 0;
              const isGroup = conversation.type === 'group';
              
              // For groups, use conversationId; for direct, use otherUserId
              const handleClick = () => {
                if (isGroup) {
                  onSelectConversation(conversation.id!, conversation.id!);
                } else {
                  const otherUser = getOtherUser(conversation);
                  if (otherUser) {
                    onSelectConversation(conversation.id!, otherUser.uid);
                  }
                }
              };

              if (isGroup) {
                return (
                  <button
                    key={conversation.id}
                    onClick={handleClick}
                    className="w-full flex items-center gap-4 p-4 md:p-6 hover:bg-slate-900/50 transition-colors text-left"
                  >
                    {/* Group Avatar */}
                    <div className="relative flex-shrink-0">
                      {conversation.photoURL ? (
                        <img
                          src={conversation.photoURL}
                          alt={conversation.title}
                          className="w-14 h-14 rounded-2xl object-cover border-2 border-slate-800"
                        />
                      ) : (
                        <div className="w-14 h-14 rounded-2xl bg-slate-800 flex items-center justify-center border-2 border-slate-700">
                          <Users className="text-slate-500" size={24} />
                        </div>
                      )}
                      {unreadForCurrentUser > 0 && (
                        <div className="absolute -top-1 -right-1 w-5 h-5 bg-cyan-500 rounded-full flex items-center justify-center">
                          <span className="text-[10px] font-black text-white">
                            {unreadForCurrentUser > 9 ? '9+' : unreadForCurrentUser}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <h3 className={`font-black text-sm truncate ${unreadForCurrentUser > 0 ? 'text-white' : 'text-slate-300'}`}>
                          {conversation.title || t('chat.group.no_name')}
                        </h3>
                        <span className="text-[10px] text-slate-600 font-bold ml-2 flex-shrink-0">
                          {formatTimestamp(conversation.lastMessageAt)}
                        </span>
                      </div>
                      <p className={`text-xs truncate ${unreadForCurrentUser > 0 ? 'text-slate-300 font-bold' : 'text-slate-500'}`}>
                        {conversation.lastMessage || t('chat.no_messages')}
                      </p>
                      <p className="text-[10px] text-slate-600 mt-1">
                        {conversation.participants.length} {t('chat.group.members').toLowerCase()}
                      </p>
                    </div>
                  </button>
                );
              } else {
                // Direct conversation
                const otherUser = getOtherUser(conversation);
                if (!otherUser) return null;

                return (
                  <button
                    key={conversation.id}
                    onClick={handleClick}
                    className="w-full flex items-center gap-4 p-4 md:p-6 hover:bg-slate-900/50 transition-colors text-left"
                  >
                    {/* Avatar */}
                    <div className="relative flex-shrink-0">
                      <img
                        src={otherUser.photoURL}
                        alt={otherUser.displayName}
                        className="w-14 h-14 rounded-2xl object-cover border-2 border-slate-800"
                      />
                      {unreadForCurrentUser > 0 && (
                        <div className="absolute -top-1 -right-1 w-5 h-5 bg-cyan-500 rounded-full flex items-center justify-center">
                          <span className="text-[10px] font-black text-white">
                            {unreadForCurrentUser > 9 ? '9+' : unreadForCurrentUser}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <h3 className={`font-black text-sm truncate ${unreadForCurrentUser > 0 ? 'text-white' : 'text-slate-300'}`}>
                          {otherUser.displayName}
                        </h3>
                        <span className="text-[10px] text-slate-600 font-bold ml-2 flex-shrink-0">
                          {formatTimestamp(conversation.lastMessageAt)}
                        </span>
                      </div>
                      <p className={`text-xs truncate ${unreadForCurrentUser > 0 ? 'text-slate-300 font-bold' : 'text-slate-500'}`}>
                        {conversation.lastMessage || t('chat.no_messages')}
                      </p>
                    </div>
                  </button>
                );
              }
            })}
          </div>
        )}
      </div>
    </div>
  );
};

